/**
 * Which pages exist in both languages, and how to get from one to the other.
 *
 * The language toggle used to change a React state value and rewrite the URL to
 * `?lang=es` without navigating. That works on exactly one page — the homepage,
 * which the hook special-cased — and does nothing anywhere else, because the
 * Spanish copy of a page is a SEPARATE FILE at a separate route
 * (`src/pages/es/...`). Flipping a state flag on the English page cannot pull
 * that file in. Measured before this existed: on `/case-studies/cantabrico` the
 * button added `?lang=es` to the address bar and left every word in English.
 *
 * So switching language is a NAVIGATION. This module holds the one fact that
 * decides where to navigate to, kept deliberately free of app imports: the hook
 * that needs it is used by Header, which is reachable from routes.tsx, so
 * importing the route table here would close a cycle.
 *
 * The pairing is purely mechanical on this site — `/x` <-> `/es/x`, no
 * translated slugs — verified against the emitted build: 19 ES routes, every one
 * with an English twin, zero orphans on either side.
 *
 * DUPLICATION WARNING: this list mirrors routes.tsx. That is a drift trap, and
 * this repo has paid for one. `src/test/locale-routes.test.ts` parses routes.tsx
 * and fails if the two disagree, so adding an `es/...` route without adding it
 * here breaks the build rather than silently shipping a toggle that 404s.
 */

/** English paths that have a Spanish twin at `/es` + path. */
export const ES_TWINNED_PATHS: ReadonlySet<string> = new Set([
  '/',
  '/about',
  '/blog',
  '/blog/3-quick-wins-automation',
  '/blog/ai-agents-for-small-business',
  '/blog/ai-automation-examples',
  '/blog/ai-automation-in-healthcare',
  '/blog/ai-for-small-business-marketing',
  '/blog/automate-manual-data-entry',
  '/blog/how-it-works',
  '/blog/what-is-agentic-ai',
  '/blog/what-is-an-ai-consultant',
  '/blog/what-is-geo-optimization',
  '/blog/what-is-vibe-coding',
  '/case-studies',
  '/case-studies/cantabrico',
  '/case-studies/splenserendo',
  '/how-we-work',
  '/privacy-policy',
]);

/**
 * Pages whose Spanish twin uses a translated slug instead of `/es` + path.
 * Keyed by English path; value is the full Spanish path.
 */
export const ES_TRANSLATED_SLUGS: Readonly<Record<string, string>> = {
  '/how-we-work': '/es/como-trabajamos',
};

/**
 * Retired service pages -> permanent (301) destination. Shared by the edge
 * middleware (server) and routes.tsx (client) so both always agree.
 */
export const RETIRED_REDIRECTS: Readonly<Record<string, string>> = {
  '/services/ai-consulting': '/how-we-work',
  '/services/ai-automation-agency': '/how-we-work',
  '/services/ai-agent-development': '/how-we-work',
  '/services/ai-marketing-consultant': '/how-we-work',
  '/es/services/ai-consulting': '/es/como-trabajamos',
  '/es/services/ai-automation-agency': '/es/como-trabajamos',
  '/es/services/ai-agent-development': '/es/como-trabajamos',
  '/es/services/ai-marketing-consultant': '/es/como-trabajamos',
};

/** vercel.json sets `trailingSlash: false`, so `/es/` is a redirect hop. Never emit one. */
export function normalizePath(pathname: string): string {
  if (!pathname || pathname === '/') return '/';
  const p = pathname.replace(/\/+$/, '');
  return p === '' ? '/' : p;
}

export function localeOf(pathname: string): 'en' | 'es' {
  const p = normalizePath(pathname);
  return p === '/es' || p.startsWith('/es/') ? 'es' : 'en';
}

/** `/es/about` -> `/about`, `/es` -> `/`, anything else unchanged. */
export function stripLocale(pathname: string): string {
  const p = normalizePath(pathname);
  for (const [en, es] of Object.entries(ES_TRANSLATED_SLUGS)) if (p === es) return en;
  if (p === '/es') return '/';
  return p.startsWith('/es/') ? p.slice(3) : p;
}

/** Does this page exist in the other language? */
export function hasTwin(pathname: string): boolean {
  return ES_TWINNED_PATHS.has(stripLocale(pathname));
}

/** The sibling URL in the other language. Only meaningful when `hasTwin` is true. */
export function alternatePath(pathname: string): string {
  const bare = stripLocale(pathname);
  if (localeOf(pathname) === 'es') return bare;
  if (ES_TRANSLATED_SLUGS[bare]) return ES_TRANSLATED_SLUGS[bare];
  return bare === '/' ? '/es' : `/es${bare}`;
}
